/**
 * Shared FCM helpers — global push for the entire ATS (not feature-page scoped).
 *
 * Global registration (App.vue / Login via `ensureFcmReady`):
 *   permission → SW register → getToken → PUT /api/user/token/:userId
 *   foreground onMessage (browser Notification for task_ping when push-only;
 *   toast + navbar inbox for other modules) + SW notificationclick routing.
 *
 * Architecture (same stack for every module):
 *   1. Each logged-in browser registers its device token via PUT /api/user/token/:userId
 *   2. Sender POSTs to backend proxy POST /api/notify/push (server resolves
 *      tokens via GET /api/user/token/:userId and sends FCM server-side)
 *
 * There is NO list-all-tokens API. Company-wide pushes
 * (`broadcastPushToActiveUsers`) fan out client-side over active `users.id`
 * rows from `allusers` / `user/list`. Ideal long-term: backend notify proxy.
 *
 * ## Push events wired on the client today
 * | module        | type                    | Sender                         | Recipient              |
 * |---------------|-------------------------|--------------------------------|------------------------|
 * | pm_tasks      | task_ping               | task-service.notifyTaskPing    | assignee emp           |
 * | pm_tasks      | task_assigned           | task-service.notifyTaskAssigned| assignee emp           |
 * | pm_tasks      | task_updated            | task-service.notifyTaskUpdated | project PM / creator   |
 * | pm_tasks      | task_completed          | task-service.notifyTaskCompleted | creator / PM / assignee |
 * | pm_tasks      | task_escalation         | task-service.notifyTaskEscalation | assignee / mgr    |
 * | pm_tasks      | task_reminder           | task-service.notifyTaskReminder| assignee / mgr         |
 * | candidates    | candidate_added         | CandidateList.vue              | account manager        |
 * | jobs          | job_assigned            | AddJob.vue                     | assigned recruiter     |
 * | announcements | hr_notice / announcement| attendance-service (broadcast) | active staff w/ token |
 * | holidays      | holiday_notice          | attendance-service (broadcast) | active staff w/ token |
 * | company_wfh   | company_wfh_notice      | attendance-service (broadcast) | active staff w/ token |
 * | invoices      | invoice_created         | backend POST /api/invoice      | creator's manager      |
 * | invoices      | invoice_due_reminder    | backend cron (due reminders) | creator's manager      |
 * | exotel        | inbound_call_unavailable| backend webhook (preferred)    | agent w/ calls OFF     |
 *
 * Payload contract for senders (required for correct deep links + inbox):
 *   data.module, data.type, entity ids (taskId, jobId, candidateId, …),
 *   optional data.link (full URL; otherwise resolved via FCM_MODULE_RESOLVERS).
 *
 * Backend should eventually own server-side send + broadcast; until then the
 * client path above is the source of truth. Graceful when permission denied.
 */

import firebaseMessaging from '@/firebase'
import {
  FCM_VAPID_KEY,
  FCM_SW_SCRIPT_URL,
  FCM_SW_SCOPE,
  firebaseConfig,
} from '@/firebase-config'
import UserService from '@/services/user-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { canAccessTaskManagement } from '@/utils/task-permissions'

export { FCM_VAPID_KEY } from '@/firebase-config'

/**
 * When true, `task_ping` is delivered via FCM / OS push only — no navbar bell
 * rows and no poll-based in-app toasts for pings. Set
 * `VUE_APP_FCM_PUSH_ONLY=false` to restore legacy in-app ping UX.
 */
export const FCM_PUSH_ONLY = String(process.env.VUE_APP_FCM_PUSH_ONLY || 'true').toLowerCase() === 'true'

/** localStorage key — `'true'` mutes foreground push chime. */
export const FCM_SOUND_MUTED_KEY = 'fcm_notification_sound_muted'

/** When false, skip chime if `document.hidden` (tab in background). Default: play anyway. */
export const FCM_SOUND_WHEN_HIDDEN = String(process.env.VUE_APP_FCM_SOUND_WHEN_HIDDEN || 'true').toLowerCase() === 'true'

export function isNotificationSoundMuted() {
  try {
    return localStorage.getItem(FCM_SOUND_MUTED_KEY) === 'true'
  } catch (e) {
    return false
  }
}

export function setNotificationSoundMuted(muted) {
  try {
    if (muted) localStorage.setItem(FCM_SOUND_MUTED_KEY, 'true')
    else localStorage.removeItem(FCM_SOUND_MUTED_KEY)
  } catch (e) { /* ignore */ }
}

/**
 * Subtle two-tone chime via Web Audio — runs in the FCM onMessage callback
 * (not subject to autoplay policy). Skipped when muted or tab hidden (if configured).
 */
export function playNotificationSound() {
  if (typeof window === 'undefined') return
  if (isNotificationSoundMuted()) return
  if (!FCM_SOUND_WHEN_HIDDEN && typeof document !== 'undefined' && document.hidden) return

  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext
    if (!AudioContext) return

    const ctx = new AudioContext()

    const playTones = () => {
      const now = ctx.currentTime
      const playTone = (freq, start, duration, volume = 0.06) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = freq
        gain.gain.setValueAtTime(0, start)
        gain.gain.linearRampToValueAtTime(volume, start + 0.015)
        gain.gain.exponentialRampToValueAtTime(0.001, start + duration)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(start)
        osc.stop(start + duration)
      }
      playTone(523.25, now, 0.14, 0.055)
      playTone(659.25, now + 0.11, 0.18, 0.045)
      setTimeout(() => { ctx.close().catch(() => {}) }, 400)
    }

    if (ctx.state === 'suspended') {
      ctx.resume().then(playTones).catch(() => {})
    } else {
      playTones()
    }
  } catch (e) { /* ignore — sound is optional UX */ }
}

/** Whether a notification type should appear in the in-app bell inbox. */
export function isInAppInboxEnabledForType(type) {
  if (!FCM_PUSH_ONLY) return true
  return String(type || '').toLowerCase() !== 'task_ping'
}

/** Task Management FCM module / type keys (pm_tasks + task_*). */
export function isTaskManagementPush(type, moduleName) {
  const m = String(moduleName || '').toLowerCase()
  if (m === 'pm_tasks') return true
  const t = String(type || '').toLowerCase()
  return t.startsWith('task_')
}

/**
 * Lazy store access — avoids vuex ↔ task-service ↔ fcm-push init cycles.
 * Use `notifications/ingestFcm` (inbox + Vuex only) — never POST back to
 * /api/notifications for payloads the server already pushed via FCM.
 */
function persistInboxNotification(notification) {
  let stored = false
  try {
    // eslint-disable-next-line global-require
    const store = require('@/store').default
    if (store && store.dispatch) {
      store.dispatch('notifications/ingestFcm', {
        notification,
        forceUnread: true,
      }).catch(err => {
        console.warn('[fcm] notifications/ingestFcm failed:', err)
      })
      stored = true
    }
  } catch (e) {
    console.warn('[fcm] store unavailable for inbox upsert:', e && e.message ? e.message : e)
  }
  if (stored) return
  try {
    // eslint-disable-next-line global-require
    const { upsertNotification } = require('@/utils/notification-inbox')
    upsertNotification(notification, { forceUnread: true })
  } catch (err) {
    console.warn('[fcm] inbox upsert failed:', err)
  }
}

/** Prod CDN logo — blocked by CORP when showing notifications from localhost. */
const FCM_PROD_ICON = 'https://ats.sattvahuman.in/img/logo.d0a9ddb2.svg'

/** Same-origin / safe icon when not on production (avoids ERR_BLOCKED_BY_RESPONSE.NotSameOrigin). */
export function getFcmDefaultIcon() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const host = window.location.hostname || ''
    // Cross-origin prod logo is blocked on localhost — omit icon (banner still shows).
    if (!/ats\.sattvahuman\.in$/i.test(host)) {
      return ''
    }
  }
  return FCM_PROD_ICON
}

/** @deprecated Prefer getFcmDefaultIcon() — kept for imports that expect a string constant. */
export const FCM_DEFAULT_ICON = FCM_PROD_ICON
/** Production deep-link host — emails/pushes are clicked outside the local app. */
export const APP_PUBLIC_BASE = 'https://ats.sattvahuman.in/'

const TASKS_HASH = '#/apps/tasks'
const ATTENDANCE_HASH = '#/dashboard/attendance'
const CALL_ANALYTICS_HASH = '#/apps/call-analytics'
const JOBS_HASH = '#/apps/jobs'
const INTERVIEW_HASH = '#/apps/interview-tracker'
const POFU_HASH = '#/apps/pofu-tracker'
const CANDIDATES_HASH = '#/apps/candidates'
const INVOICE_DASHBOARD_HASH = '#/apps/invoice/dashboard'

/** Session-only inbox sources preserved across API `replace` refresh. */
export const FCM_PRESERVED_INBOX_SOURCES = new Set(['fcm', 'hr_local'])

/**
 * Extensible module registry — add new ATS features here (module and/or type key).
 * Each resolver receives stringified FCM `data` fields and returns a full URL.
 */
export const FCM_MODULE_RESOLVERS = {
  pm_tasks: d => buildTasksDeepLink(d.taskId),
  task_ping: d => buildTasksDeepLink(d.taskId),
  task_escalation: d => buildTasksDeepLink(d.taskId),
  task_reminder: d => buildTasksDeepLink(d.taskId),
  task_completed: d => buildTasksDeepLink(d.taskId),
  task_notice: d => buildTasksDeepLink(d.taskId),
  task_assignment: d => buildTasksDeepLink(d.taskId),
  announcements: () => buildAttendanceDeepLink(),
  holidays: () => buildAttendanceDeepLink(),
  company_wfh: () => buildAttendanceDeepLink(),
  attendance: () => buildAttendanceDeepLink(),
  hr_notice: () => buildAttendanceDeepLink(),
  exotel: () => buildCallAnalyticsDeepLink({ direction: 'inbound' }),
  inbound_call: () => buildCallAnalyticsDeepLink({ direction: 'inbound' }),
  inbound_call_unavailable: () => buildCallAnalyticsDeepLink({ direction: 'inbound' }),
  jobs: d => buildJobDeepLink(d.jobId || d.job_id),
  job: d => buildJobDeepLink(d.jobId || d.job_id),
  job_update: d => buildJobDeepLink(d.jobId || d.job_id),
  job_timeline: d => buildJobDeepLink(d.jobId || d.job_id),
  updates: d => buildJobDeepLink(d.jobId || d.job_id),
  interview: d => buildInterviewTrackerDeepLink(d),
  interview_tracker: d => buildInterviewTrackerDeepLink(d),
  pofu: d => buildPofuTrackerDeepLink(d),
  pofu_tracker: d => buildPofuTrackerDeepLink(d),
  candidate: d => buildCandidateDeepLink(d.candidateId || d.candidate_id, d.jobId || d.job_id),
  candidates: d => buildCandidateDeepLink(d.candidateId || d.candidate_id, d.jobId || d.job_id),
  invoices: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  invoice: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  invoice_created: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  invoice_due_reminder: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  due_reminder: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  due_today: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  overdue_7: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  overdue_14: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  overdue_30: d => buildInvoiceDeepLink(d.invoiceId || d.invoice_id),
  general: () => `${APP_PUBLIC_BASE}#/`,
  fcm_verify: () => `${APP_PUBLIC_BASE}#/`,
  test: () => `${APP_PUBLIC_BASE}#/`,
}

/** Modules that use sticky (non-auto-dismiss) foreground toasts. */
const STICKY_TOAST_MODULES = new Set([
  'pm_tasks',
  'announcements',
  'holidays',
  'company_wfh',
  'exotel',
  'inbound_call',
  'inbound_call_unavailable',
  'interview',
  'interview_tracker',
  'pofu',
  'pofu_tracker',
  'invoices',
  'invoice',
])

let foregroundListenerBound = false
/** Latest Vue root / layout host for toast + router (updated on each ensureFcmReady). */
let foregroundHost = null
let registrationInFlight = null
/** Avoid PUT /user/token on every route change once this session succeeded. */
let lastRegistered = { userId: null, token: null }
let swMessageListenerBound = false
let tokenRefreshListenerBound = false

/**
 * Re-PUT device token when Firebase rotates it mid-session.
 * Clears the session cache so registerFcmTokenForCurrentUser persists the new token.
 */
function bindFcmTokenRefreshHandler() {
  if (tokenRefreshListenerBound) return
  if (!firebaseMessaging || typeof firebaseMessaging.onTokenRefresh !== 'function') return
  tokenRefreshListenerBound = true
  firebaseMessaging.onTokenRefresh(() => {
    lastRegistered = { userId: null, token: null }
    registerFcmTokenForCurrentUser().catch(() => {})
  })
}

/**
 * Single entry point for global FCM — call after any auth path sets userData
 * (Login.vue, App.vue mount/route, page refresh with session). Idempotent.
 */
export function ensureFcmReady(vm) {
  if (typeof window === 'undefined') return
  try {
    if (!localStorage.getItem('userData')) return
  } catch (e) {
    return
  }
  if (vm) foregroundHost = vm
  bindFcmServiceWorkerMessages(vm)
  bindFcmTokenRefreshHandler()
  setupForegroundFcmHandler(vm)
  registerFcmTokenForCurrentUser().catch(() => {})
}

/**
 * Navigate the open SPA to an FCM deep link (hash or path). Used by toast click
 * and service-worker `notificationclick` postMessage — works from any page.
 */
export function navigateFromFcmLink(vm, link) {
  if (!link) return
  try {
    const hashIdx = String(link).indexOf('#')
    const router = (vm && vm.$router) || (foregroundHost && foregroundHost.$router)
    if (hashIdx >= 0 && router) {
      const hashPath = String(link).slice(hashIdx + 1) || '/'
      router.push(hashPath).catch(() => {
        window.location.href = link
      })
      return
    }
    if (router && String(link).startsWith('/')) {
      router.push(link).catch(() => {
        window.location.href = link
      })
      return
    }
    window.location.href = link
  } catch (e) {
    try { window.location.href = link } catch (err) { /* ignore */ }
  }
}

/**
 * Listen for SW `notificationclick` → postMessage so an already-open tab
 * routes correctly (hash SPA; client.navigate often no-ops on hash-only changes).
 * Idempotent; safe to call from App.vue on mount.
 */
export function bindFcmServiceWorkerMessages(vm) {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return
  if (vm) foregroundHost = vm
  if (swMessageListenerBound) return
  swMessageListenerBound = true
  navigator.serviceWorker.addEventListener('message', event => {
    const data = event && event.data
    if (!data) return
    if (data.type === 'FCM_NOTIFICATION_CLICK') {
      navigateFromFcmLink(foregroundHost, data.link)
      return
    }
    // Background SW received a push — mirror into navbar Notifications while a tab is open.
    if (data.type === 'FCM_INBOX_UPSERT' && data.payload) {
      ingestFcmPayloadToInbox(data.payload)
    }
  })
}

export function buildTasksDeepLink(taskId) {
  if (taskId == null || taskId === '') return `${APP_PUBLIC_BASE}${TASKS_HASH}`
  return `${APP_PUBLIC_BASE}${TASKS_HASH}?taskId=${encodeURIComponent(taskId)}`
}

/** Attendance Dashboard deep-link (announcements / holidays / company WFH). */
export function buildAttendanceDeepLink() {
  return `${APP_PUBLIC_BASE}${ATTENDANCE_HASH}`
}

/** Call Analytics deep-link (inbound missed while Start Calling is OFF). */
export function buildCallAnalyticsDeepLink({ direction = 'inbound' } = {}) {
  const qs = direction ? `?direction=${encodeURIComponent(direction)}` : ''
  return `${APP_PUBLIC_BASE}${CALL_ANALYTICS_HASH}${qs}`
}

/** Jobs list / job detail deep-link. */
export function buildJobDeepLink(jobId) {
  if (jobId != null && jobId !== '') {
    return `${APP_PUBLIC_BASE}#/apps/view-job/${encodeURIComponent(jobId)}`
  }
  return `${APP_PUBLIC_BASE}${JOBS_HASH}`
}

/** Candidate profile deep-link (requires job context when available). */
export function buildCandidateDeepLink(candidateId, jobId) {
  if (candidateId != null && candidateId !== '' && jobId != null && jobId !== '') {
    return `${APP_PUBLIC_BASE}#/apps/candidates/view/${encodeURIComponent(candidateId)}/${encodeURIComponent(jobId)}`
  }
  return `${APP_PUBLIC_BASE}${CANDIDATES_HASH}`
}

/** Interview Tracker deep-link. */
export function buildInterviewTrackerDeepLink(params = {}) {
  const qs = new URLSearchParams()
  if (params.interviewId || params.interview_id) {
    qs.set('id', String(params.interviewId || params.interview_id))
  }
  if (params.jobId || params.job_id) qs.set('jobId', String(params.jobId || params.job_id))
  if (params.candidateId || params.candidate_id) {
    qs.set('candidateId', String(params.candidateId || params.candidate_id))
  }
  const q = qs.toString()
  return `${APP_PUBLIC_BASE}${INTERVIEW_HASH}${q ? `?${q}` : ''}`
}

/** POFU Tracker deep-link. */
export function buildPofuTrackerDeepLink(params = {}) {
  const filter = params.filter || params.candidateId || params.candidate_id
  const qs = filter ? `?filter=${encodeURIComponent(filter)}` : ''
  return `${APP_PUBLIC_BASE}${POFU_HASH}${qs}`
}

/** Invoice dashboard / update deep-link. */
export function buildInvoiceDeepLink(invoiceId) {
  if (invoiceId != null && invoiceId !== '') {
    return `${APP_PUBLIC_BASE}#/apps/update-invoice/${encodeURIComponent(String(invoiceId))}`
  }
  return `${APP_PUBLIC_BASE}${INVOICE_DASHBOARD_HASH}`
}

function inferModuleFromType(type) {
  const t = String(type || '').toLowerCase()
  if (!t) return ''
  if (t.includes('interview')) return 'interview_tracker'
  if (t.includes('pofu')) return 'pofu_tracker'
  if (t.includes('task') || t.includes('pm_')) return 'pm_tasks'
  if (t.includes('announcement') || t.includes('holiday') || t.includes('wfh')) return 'announcements'
  if (t.includes('attendance')) return 'attendance'
  if (t.includes('inbound_call') || t.includes('exotel')) return 'exotel'
  if (t.includes('candidate')) return 'candidates'
  if (t.includes('invoice') || t.startsWith('due_') || t.startsWith('overdue_')) return 'invoices'
  if (t.includes('job')) return 'jobs'
  return ''
}

/**
 * Resolve deep link from any FCM payload (notification + data).
 * Prefer explicit link/click_action; fall back to module/type registry.
 */
export function resolveFcmDeepLink(data = {}, notification = {}) {
  const link = data.link || data.click_action || notification.click_action
  if (link) return String(link)

  const moduleName = data.module || inferModuleFromType(data.type)
  const type = data.type || ''
  const resolver = FCM_MODULE_RESOLVERS[moduleName] || FCM_MODULE_RESOLVERS[type]
  if (resolver) return resolver(data)

  if (data.candidateId || data.candidate_id) {
    return buildCandidateDeepLink(
      data.candidateId || data.candidate_id,
      data.jobId || data.job_id,
    )
  }
  if (data.jobId || data.job_id) return buildJobDeepLink(data.jobId || data.job_id)
  if (data.taskId) return buildTasksDeepLink(data.taskId)

  return `${APP_PUBLIC_BASE}#/`
}

/** Normalize FCM payload → { title, body, link, moduleName, type, data, sticky, icon, variant }. */
export function resolveFcmPayload(payload) {
  const notification = (payload && payload.notification) || {}
  const data = (payload && payload.data) || {}
  const title = notification.title || data.title || 'Notification'
  const body = notification.body || data.body || ''
  const type = data.type || inferModuleFromType(data.module) || ''
  const isInboundCall = type === 'inbound_call_unavailable' || type === 'inbound_call'
    || data.module === 'exotel' || data.module === 'inbound_call'
  const moduleName = data.module || inferModuleFromType(type) || (isInboundCall ? 'exotel' : 'general')
  const link = resolveFcmDeepLink(data, notification)
  const sticky = STICKY_TOAST_MODULES.has(moduleName) || STICKY_TOAST_MODULES.has(type)
  let icon = 'BellIcon'
  let variant = 'light-primary'
  if (moduleName === 'holidays') icon = 'CalendarIcon'
  else if (moduleName === 'company_wfh') icon = 'HomeIcon'
  else if (isInboundCall) {
    icon = 'PhoneIncomingIcon'
    variant = 'light-warning'
  } else if (type === 'task_escalation') variant = 'light-danger'
  else if (type === 'task_completed') {
    icon = 'CheckCircleIcon'
    variant = 'light-success'
  }
  else if (/interview/i.test(type) || /interview/.test(moduleName)) icon = 'CalendarIcon'
  else if (/pofu/i.test(type) || /pofu/.test(moduleName)) icon = 'UserCheckIcon'
  else if (/job|update/i.test(type) || /job/.test(moduleName)) icon = 'BriefcaseIcon'
  else if (type === 'fcm_verify' || type === 'test') {
    icon = 'BellIcon'
    variant = 'light-info'
  }

  const fcmMessageId = (payload && (payload.fcmMessageId || payload.messageId))
    || data.fcmMessageId
    || data.messageId
    || ''

  return {
    title,
    body,
    link,
    moduleName,
    type,
    data,
    sticky,
    icon,
    variant: isInboundCall ? 'light-warning' : variant,
    isInboundCall,
    fcmMessageId: fcmMessageId ? String(fcmMessageId) : '',
  }
}

/** Strip full URL to hash-router path for inbox storage. */
export function fcmLinkToRouterPath(link) {
  if (!link) return null
  const s = String(link)
  const hashIdx = s.indexOf('#')
  if (hashIdx >= 0) return s.slice(hashIdx + 1) || '/'
  if (s.startsWith('/')) return s
  return null
}

function isActiveUserRow(u) {
  if (!u) return false
  const s = u.status ?? u.employee_status ?? u.is_active
  if (s == null || s === '') return true
  return s === 'Active' || s === 'active' || s === true || s === 'ACTIVE' || s === 1 || s === '1'
}

function readUsersFromLocalCaches() {
  const caches = ['allusers', 'users']
  for (let c = 0; c < caches.length; c++) {
    try {
      const raw = JSON.parse(localStorage.getItem(caches[c]) || 'null')
      const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.data) ? raw.data : [])
      if (list.length) return list
    } catch (e) { /* ignore */ }
  }
  return []
}

/**
 * Active staff `users.id` values for company-wide FCM fan-out.
 * Prefer login-populated `allusers`; empty list means caller may fetch.
 */
export function listBroadcastUserIds({ excludeUserId = null } = {}) {
  const list = readUsersFromLocalCaches()
  const ids = []
  const seen = new Set()
  list.forEach(u => {
    if (!u || u.id == null || !isActiveUserRow(u)) return
    const id = u.id
    if (excludeUserId != null && String(id) === String(excludeUserId)) return
    const key = String(id)
    if (seen.has(key)) return
    seen.add(key)
    ids.push(id)
  })
  return ids
}

async function resolveBroadcastUserIds({ excludeUserId = null } = {}) {
  let ids = listBroadcastUserIds({ excludeUserId })
  if (ids.length) return ids

  // Fallback when localStorage caches are empty (e.g. cold session).
  try {
    const res = await UserService.getUserList()
    const raw = res && (res.data?.data ?? res.data)
    const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.data) ? raw.data : [])
    const seen = new Set()
    list.forEach(u => {
      if (!u || u.id == null || !isActiveUserRow(u)) return
      if (excludeUserId != null && String(u.id) === String(excludeUserId)) return
      const key = String(u.id)
      if (seen.has(key)) return
      seen.add(key)
      ids.push(u.id)
    })
  } catch (err) {
    console.warn('[fcm] getUserList fallback failed:', err)
  }
  return ids
}

async function mapPool(items, concurrency, fn) {
  const results = new Array(items.length)
  let next = 0
  async function worker() {
    while (next < items.length) {
      const idx = next
      next += 1
      try {
        results[idx] = await fn(items[idx], idx)
      } catch (err) {
        results[idx] = { sent: 0, reason: 'error' }
      }
    }
  }
  const n = Math.min(Math.max(1, concurrency), Math.max(1, items.length))
  await Promise.all(Array.from({ length: n }, () => worker()))
  return results
}

/**
 * Fan-out push to many user ids (bounded concurrency). Never throws.
 * Skips users with no registered tokens.
 */
export async function sendPushToManyUserIds(userIds, options = {}) {
  const ids = Array.isArray(userIds) ? userIds.filter(id => id != null && id !== '') : []
  if (!ids.length) {
    return { sent: 0, users: 0, reason: 'no_recipients' }
  }
  const concurrency = options.concurrency > 0 ? options.concurrency : 5
  const results = await mapPool(ids, concurrency, userId => sendPushToUserId(userId, options))
  const sent = results.reduce((sum, r) => sum + ((r && r.sent) || 0), 0)
  return {
    sent,
    users: ids.length,
    reason: sent ? undefined : 'no_tokens_or_send_failed',
  }
}

/**
 * Company-wide push: active employees with FCM tokens (same broad audience
 * as sattva-all email intent). Fire-and-forget friendly — never throws.
 *
 * Audience strategy (document for ops/backend):
 * - Email for announcements goes to sattva-all@… (distribution list).
 * - Push cannot target that list; client fans out over active user ids +
 *   GET /api/user/token/:id. Prefer a backend broadcast endpoint later.
 */
export async function broadcastPushToActiveUsers(options = {}) {
  try {
    let excludeUserId = options.excludeUserId
    if (excludeUserId === undefined) {
      try {
        const me = JSON.parse(localStorage.getItem('userData') || 'null')
        excludeUserId = me && me.id != null ? me.id : null
      } catch (e) {
        excludeUserId = null
      }
    }
    const ids = await resolveBroadcastUserIds({ excludeUserId })
    if (!ids.length) {
      return { sent: 0, users: 0, reason: 'no_recipients' }
    }
    return sendPushToManyUserIds(ids, options)
  } catch (err) {
    console.warn('[fcm] broadcastPushToActiveUsers failed:', err)
    return { sent: 0, users: 0, reason: 'error' }
  }
}

function upsertInboxFromFcmPayload(resolved) {
  try {
    const {
      title, body, link, moduleName, type, data, icon, variant, fcmMessageId,
    } = resolved
    if (!isInAppInboxEnabledForType(type)) return
    const metaId = data.announcementId || data.holidayId || data.companyWfhId
      || data.taskId || data.jobId || data.job_id
      || data.candidateId || data.candidate_id || data.interviewId || data.interview_id
      || data.call_sid || data.callSid || data.sid || ''
    // Unique per delivery so each push appears as a new bell row (esp. fcm_verify tests).
    const deliveryId = fcmMessageId || data.fcmMessageId || `${Date.now()}`
    const idParts = [
      'fcm',
      type || moduleName || 'general',
      metaId,
      deliveryId,
    ].filter(Boolean)
    const id = idParts.join(':').slice(0, 180)
    const dedupeKey = metaId
      ? `${type || moduleName}:${metaId}`
      : id
    persistInboxNotification({
      id,
      type: String(type || moduleName || 'general'),
      title: title || 'Notification',
      message: body || '',
      createdAt: new Date().toISOString(),
      read: false,
      link: fcmLinkToRouterPath(link),
      icon: icon || 'BellIcon',
      variant: variant || 'light-primary',
      source: 'fcm',
      meta: { module: moduleName, dedupeKey, fcmMessageId: deliveryId, ...data },
    })
  } catch (err) {
    console.warn('[fcm] inbox upsert failed:', err)
  }
}

/**
 * Public entry: normalize any FCM payload and add it to the navbar Notifications drawer.
 * Used by foreground onMessage and by SW → client postMessage when the tab is backgrounded.
 */
export function ingestFcmPayloadToInbox(payload) {
  if (!payload) return
  try {
    const resolved = resolveFcmPayload(payload)
    if (isTaskManagementPush(resolved.type, resolved.moduleName) && !canAccessTaskManagement()) {
      return
    }
    upsertInboxFromFcmPayload(resolved)
  } catch (err) {
    console.warn('[fcm] ingestFcmPayloadToInbox failed:', err)
  }
}

/**
 * Resolve numeric/string `users.id` for FCM token APIs from an `emp_id`.
 * Uses local caches first (same sources Login / task team already populate).
 */
export function resolveUserIdFromEmpId(empId, hintUserId = null) {
  if (hintUserId != null && hintUserId !== '') return hintUserId
  if (empId == null || empId === '') return null

  const matchEmpId = row => {
    if (!row) return false
    const eid = row.emp_id ?? row.empId ?? row.employee_id
    return eid != null && String(eid) === String(empId)
  }

  try {
    const me = JSON.parse(localStorage.getItem('userData') || 'null')
    if (matchEmpId(me) && me.id != null) {
      return me.id
    }
  } catch (e) { /* ignore */ }

  const caches = ['allusers', 'users']
  for (let c = 0; c < caches.length; c++) {
    try {
      const raw = JSON.parse(localStorage.getItem(caches[c]) || 'null')
      const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.data) ? raw.data : [])
      const row = list.find(matchEmpId)
      if (row && row.id != null) return row.id
    } catch (e) { /* ignore */ }
  }
  return null
}

/** Human-readable manager toast when POST /api/notify/push returns sent: 0. */
export function formatPushFailureMessage(result) {
  if (!result || Number(result.sent) > 0) return null
  const reason = result.reason || 'unknown'
  const byReason = {
    no_tokens: 'employee has no registered push device — ask them to allow notifications and reload the app',
    backend_unavailable: 'push service is unavailable on the server',
    unresolved_user: 'could not resolve the employee user account for push',
    no_user_id: 'no employee user id or emp id was available for push',
    no_recipient: 'no push recipient was specified',
    send_failed: result.message || 'push was rejected by the server',
    error: 'network error while sending push',
  }
  return byReason[reason] || result.message || 'push could not be delivered'
}

/**
 * Show an OS-level notification while the app is focused.
 * Prefers serviceWorker.showNotification (more reliable than new Notification()).
 */
export async function showOsNotification(title, body, { link, icon } = {}) {
  if (typeof window === 'undefined') return false
  const safeTitle = title || 'Notification'
  const resolvedIcon = icon || getFcmDefaultIcon()
  const options = {
    body: body || '',
    silent: false,
    data: { link: link || '' },
  }
  // Only set icon when we have a URL; omit on failure-prone cross-origin assets.
  if (resolvedIcon) options.icon = resolvedIcon

  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready
      if (reg && typeof reg.showNotification === 'function') {
        await reg.showNotification(safeTitle, options)
        return true
      }
    } catch (err) {
      console.warn('[fcm] serviceWorker.showNotification failed:', err)
    }
  }

  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      const n = new Notification(safeTitle, options)
      n.onclick = () => {
        window.focus()
        navigateFromFcmLink(foregroundHost, link)
        n.close()
      }
      return true
    } catch (err) {
      console.warn('[fcm] Notification constructor failed:', err)
    }
  }
  return false
}

function extractTokenRows(res) {
  if (!res) return []
  if (Array.isArray(res)) return res
  if (Array.isArray(res.data)) return res.data
  if (res.status === 'OK' && Array.isArray(res.data)) return res.data
  const row = res.data && typeof res.data === 'object' ? res.data : res
  if (row && (row.fcm_token || row.token)) return [row]
  return []
}

/**
 * Map Firebase getToken / subscribe errors to actionable remediation hints.
 * `messaging/token-subscribe-failed` + missing OAuth credential is almost always
 * Google Cloud API enablement or Browser API key restrictions — not app code.
 */
export function interpretFcmRegistrationError(err) {
  const code = err && err.code != null ? String(err.code) : ''
  const message = err && err.message != null ? String(err.message) : String(err || '')
  const combined = `${code} ${message}`.toLowerCase()

  if (combined.includes('token-subscribe-failed') || combined.includes('authentication credential')) {
    return {
      reason: 'fcm_api_auth',
      userMessage: 'FCM subscription rejected: Google Cloud auth credential missing or invalid.',
      remediation: [
        `Enable "Firebase Cloud Messaging API" and "Firebase Installations API" in Google Cloud Console for project "${firebaseConfig.projectId}".`,
        'In Google Cloud Console → APIs & Services → Credentials, edit the Browser API key used by this web app: allow HTTP referrers for your domain (e.g. https://ats.sattvahuman.in/* and http://localhost:* for dev), and under API restrictions include Firebase Installations API + FCM Registration API (or use "Don\'t restrict key" while testing).',
        'In Firebase Console → Project Settings → Cloud Messaging → Web Push certificates, copy the active key pair public key into FCM_VAPID_KEY in src/firebase-config.js.',
        'Confirm public/firebase-messaging-sw.js firebaseConfig matches src/firebase-config.js (same projectId, appId, messagingSenderId).',
      ],
    }
  }

  if (combined.includes('messaging/permission-blocked') || combined.includes('permission-blocked')) {
    return {
      reason: 'denied',
      userMessage: 'Notification permission is blocked in the browser.',
      remediation: [
        'Reset site notification permission in browser settings, then reload and allow notifications when prompted.',
      ],
    }
  }

  if (combined.includes('messaging/unsupported-browser') || combined.includes('unsupported-browser')) {
    return {
      reason: 'unsupported',
      userMessage: 'This browser does not support Firebase Cloud Messaging.',
      remediation: [
        'Use a supported browser over HTTPS (Chrome, Firefox, Edge). Safari requires extra setup.',
      ],
    }
  }

  if (combined.includes('failed-service-worker-registration') || combined.includes('serviceworker')) {
    return {
      reason: 'sw_registration_failed',
      userMessage: 'Firebase messaging service worker could not be registered.',
      remediation: [
        `Ensure ${FCM_SW_SCRIPT_URL} is deployed at the site root with scope ${FCM_SW_SCOPE} and matches src/firebase-config.js.`,
        'Hard-refresh or unregister old service workers in DevTools → Application → Service Workers.',
      ],
    }
  }

  return {
    reason: 'error',
    userMessage: message || code || 'Unknown FCM registration error',
    remediation: null,
  }
}

/**
 * Request Notification permission + register this device's FCM token for the
 * logged-in user. Intended to run globally from App.vue / Login (not feature
 * pages). Safe to call repeatedly; caches success per user/token for the
 * session. Returns `{ ok, reason?, token? }` — never throws.
 *
 * Backend: tokens are stored per `users.id` via PUT /api/user/token/:userId
 * (global device row — not module-scoped). Send path still fans out client-
 * side; a server notify proxy would be ideal later.
 */
export async function registerFcmTokenForCurrentUser() {
  if (registrationInFlight) return registrationInFlight

  registrationInFlight = (async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        return { ok: false, reason: 'unsupported' }
      }
      if (!firebaseMessaging || typeof firebaseMessaging.getToken !== 'function') {
        return { ok: false, reason: 'messaging_unavailable' }
      }

      let permission = Notification.permission
      if (permission === 'default') {
        permission = await Notification.requestPermission()
      }
      if (permission !== 'granted') {
        return { ok: false, reason: 'denied' }
      }

      // Absolute path so registration works with vue publicPath: '.' in prod.
      // Unregister any broken CDN-based SW (gstatic importScripts) before registering.
      let swReg
      try {
        if (navigator.serviceWorker.getRegistrations) {
          const regs = await navigator.serviceWorker.getRegistrations()
          await Promise.all((regs || []).map(async (reg) => {
            const scriptURL = (reg.active && reg.active.scriptURL)
              || (reg.waiting && reg.waiting.scriptURL)
              || (reg.installing && reg.installing.scriptURL)
              || ''
            // Drop stale workers that still point at the unversioned SW URL only.
            if (scriptURL && scriptURL.indexOf('firebase-messaging-sw.js') >= 0
              && scriptURL.indexOf('v=vendor-2') < 0) {
              try {
                await reg.unregister()
                console.warn('[fcm] Unregistered stale messaging SW:', scriptURL)
              } catch (e) { /* ignore */ }
            }
          }))
        }
        swReg = await navigator.serviceWorker.register(FCM_SW_SCRIPT_URL, {
          scope: FCM_SW_SCOPE,
          updateViaCache: 'none',
        })
        if (swReg && typeof swReg.update === 'function') {
          try { await swReg.update() } catch (e) { /* ignore */ }
        }
        await navigator.serviceWorker.ready
      } catch (swErr) {
        console.warn('[fcm] SW register failed (will still try getToken):', swErr)
        console.warn(
          '[fcm] If you see gstatic.com CSP errors, production is still serving the OLD SW. '
          + 'Deploy /firebase-messaging-sw.js + /vendor/firebase/*.js then hard-refresh.',
        )
      }

      const tokenOptions = { vapidKey: FCM_VAPID_KEY }
      if (swReg) tokenOptions.serviceWorkerRegistration = swReg
      const currentToken = await firebaseMessaging.getToken(tokenOptions)
      if (!currentToken) {
        return { ok: false, reason: 'no_token' }
      }

      const userData = JSON.parse(localStorage.getItem('userData') || 'null')
      const userId = userData && userData.id
      if (userId == null) {
        return { ok: false, reason: 'no_user', token: currentToken }
      }

      if (
        lastRegistered.userId != null
        && String(lastRegistered.userId) === String(userId)
        && lastRegistered.token === currentToken
      ) {
        return { ok: true, token: currentToken, reason: 'cached' }
      }

      const res = await UserService.setToken(userId, { token: currentToken })
      const ok = res && (res.status === 'OK' || res.httpStatus === 201)
      if (ok) {
        lastRegistered = { userId, token: currentToken }
        return { ok: true, token: currentToken }
      }
      if (res && (res.status === 'FORBIDDEN' || res.httpStatus === 403)) {
        warnFcmTokenRegisterOnce(
          '[fcm] FCM token not saved — server denied permission. Deploy backend fix for PUT /api/user/token/:id (self-update) or grant update on users.',
        )
        return { ok: false, token: currentToken, reason: 'permission_denied' }
      }
      return { ok: false, token: currentToken, reason: 'set_token_failed' }
    } catch (err) {
      const interpreted = interpretFcmRegistrationError(err)
      console.warn('[fcm] registerFcmTokenForCurrentUser failed:', interpreted.userMessage, err)
      if (interpreted.remediation && interpreted.remediation.length) {
        console.warn('[fcm] Fix in Firebase / Google Cloud Console:')
        interpreted.remediation.forEach(step => console.warn(`  • ${step}`))
      }
      return {
        ok: false,
        reason: interpreted.reason,
        detail: interpreted.userMessage,
        remediation: interpreted.remediation,
      }
    } finally {
      registrationInFlight = null
    }
  })()

  return registrationInFlight
}

/** Clear session cache on logout so the next user re-registers. */
export function resetFcmRegistrationCache() {
  lastRegistered = { userId: null, token: null }
}

/** Probed once per session — skip repeat calls when POST /api/notify/push is missing. */
let notifyPushEndpointSupported = null
let notifyPushEndpointWarned = false
let fcmTokenRegisterWarned = false

function warnNotifyPushOnce(message) {
  if (notifyPushEndpointWarned) return
  notifyPushEndpointWarned = true
  console.warn(message)
}

function warnFcmTokenRegisterOnce(message) {
  if (fcmTokenRegisterWarned) return
  fcmTokenRegisterWarned = true
  console.warn(message)
}

function buildNotifyPushData({ title, body, link, data = {} }) {
  const dataPayload = {}
  Object.keys(data || {}).forEach(key => {
    if (data[key] != null) dataPayload[key] = String(data[key])
  })
  const clickAction = link || resolveFcmDeepLink(dataPayload)
  dataPayload.link = String(clickAction)
  dataPayload.click_action = String(clickAction)
  if (title) dataPayload.title = String(title)
  if (body) dataPayload.body = String(body)
  if (!dataPayload.module && dataPayload.type) {
    dataPayload.module = inferModuleFromType(dataPayload.type) || 'general'
  }
  return dataPayload
}

/**
 * Send an FCM notification to a user via POST /api/notify/push (server-side
 * token lookup + FCM). Fire-and-forget friendly — never throws.
 */
export async function sendPushToUserId(userId, {
  title,
  body,
  link,
  data = {},
  empIds = [],
} = {}) {
  const resolvedEmpIds = Array.isArray(empIds) ? empIds.filter(Boolean) : []
  const hasUser = userId != null && userId !== ''
  if (!hasUser && !resolvedEmpIds.length) {
    return { sent: 0, reason: 'no_user_id' }
  }
  if (!title) {
    return { sent: 0, reason: 'no_title' }
  }
  if (notifyPushEndpointSupported === false) {
    return { sent: 0, reason: 'backend_unavailable' }
  }

  try {
    const dataPayload = buildNotifyPushData({ title, body, link, data })
    const res = await UserService.sendNotifyPush({
      userIds: hasUser ? [userId] : [],
      empIds: resolvedEmpIds,
      title,
      body: body || '',
      data: dataPayload,
    })

    if (res && res.status === 'OK') {
      notifyPushEndpointSupported = true
      const sent = Number(res.sent) || 0
      if (!sent) {
        console.warn('[fcm] POST /api/notify/push OK but sent: 0 — no device tokens for recipient', {
          userIds: hasUser ? [userId] : [],
          empIds: resolvedEmpIds,
          message: res.message,
        })
      }
      return {
        sent,
        reason: sent ? undefined : 'no_tokens',
        message: sent ? undefined : (res.message || 'No registered device tokens'),
      }
    }

    // Route missing (404/501) — skip repeat calls for the session. Do NOT cache
    // 503 NOT_IMPLEMENTED (FCM credentials missing): route exists; keep calling
    // so server logs stay visible and pushes work once FCM is configured.
    const routeMissing = res && (res.httpStatus === 404 || res.httpStatus === 501)
    if (routeMissing) {
      notifyPushEndpointSupported = false
      warnNotifyPushOnce(
        '[fcm] POST /api/notify/push not available — push skipped until backend implements server-side FCM (see backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md §5.4)',
      )
      return { sent: 0, reason: 'backend_unavailable', message: res.message }
    }

    if (res && res.status === 'NOT_IMPLEMENTED') {
      console.warn('[fcm] POST /api/notify/push returned NOT_IMPLEMENTED — FCM likely not configured on server', {
        userIds: hasUser ? [userId] : [],
        empIds: resolvedEmpIds,
        httpStatus: res.httpStatus,
        message: res.message,
      })
      return { sent: 0, reason: 'backend_unavailable', message: res.message }
    }

    console.warn('[fcm] POST /api/notify/push failed:', {
      userIds: hasUser ? [userId] : [],
      empIds: resolvedEmpIds,
      status: res && res.status,
      message: res && (res.message || res.detail),
    })
    return {
      sent: 0,
      reason: 'send_failed',
      message: (res && (res.message || res.detail)) || undefined,
    }
  } catch (err) {
    console.warn('[fcm] sendPushToUserId failed:', err)
    return { sent: 0, reason: 'error', message: err && err.message }
  }
}

/**
 * Resolve emp_id → user id (optional hint), then send push.
 */
export async function sendPushToEmpId(empId, options = {}) {
  const userId = resolveUserIdFromEmpId(empId, options.userId)
  if (userId == null && (empId == null || empId === '')) {
    console.warn('[fcm] skipped — no emp_id and could not resolve user id', empId)
    return { sent: 0, reason: 'unresolved_user' }
  }
  if (userId == null && empId != null && empId !== '') {
    console.warn('[fcm] users.id unresolved for emp_id — POST /api/notify/push will use empIds only', empId)
  }
  return sendPushToUserId(userId, {
    ...options,
    empIds: empId != null && empId !== '' ? [String(empId)] : [],
  })
}

/**
 * Bind a single foreground onMessage handler (idempotent, app-wide).
 * Toast + inbox badge for all modules; sticky for tasks / HR broadcasts.
 * Re-calling with a new vm only updates the toast/router host.
 */
export function setupForegroundFcmHandler(vm) {
  if (vm) foregroundHost = vm
  if (foregroundListenerBound) return
  if (!firebaseMessaging || typeof firebaseMessaging.onMessage !== 'function') return

  foregroundListenerBound = true
  firebaseMessaging.onMessage(payload => {
    try {
      const host = foregroundHost
      const resolved = resolveFcmPayload(payload)
      const {
        title, body, link, moduleName, type, data, sticky, icon, variant,
      } = resolved

      // Same ACL as Tasks nav/route — no task push UX without access.
      if (isTaskManagementPush(type, moduleName) && !canAccessTaskManagement()) {
        return
      }

      playNotificationSound()

      // Navbar bell — skip task_ping when FCM_PUSH_ONLY (OS push is enough).
      upsertInboxFromFcmPayload(resolved)

      const typeLower = String(type || '').toLowerCase()
      const pushOnlyPing = FCM_PUSH_ONLY && typeLower === 'task_ping'
      // CLI / script tests (scripts/test-fcm-push.js) — always show OS tray, not toast-only.
      const isVerifyPush = typeLower === 'fcm_verify' || typeLower === 'test'

      if (typeLower === 'inbound_call_unavailable') {
        import('@/utils/exotel-agent-availability').then(({ applyRemoteAvailability }) => {
          applyRemoteAvailability(false)
        }).catch(() => {})
      }

      // Foreground task_ping in push-only mode: OS notification, not bell/toast.
      if (pushOnlyPing || isVerifyPush) {
        showOsNotification(title, body, { link }).then(shown => {
          if (!shown) {
            console.warn('[fcm] foreground OS notification not shown — permission denied or API blocked', {
              permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
              title,
              type: typeLower,
            })
          }
        }).catch(err => {
          console.warn('[fcm] foreground showOsNotification error:', err)
        })
        if (pushOnlyPing) return
        // fcm_verify: also toast below when host exists
      }

      if (host && typeof host.$toast === 'function' && !pushOnlyPing) {
        const toastId = sticky
          ? `fcm-${moduleName || 'notice'}-${type || 'x'}-${data.announcementId || data.holidayId || data.companyWfhId || data.taskId || data.jobId || data.call_sid || title}`
          : undefined
        host.$toast({
          component: ToastificationContent,
          props: {
            title,
            text: body,
            icon,
            variant: variant === 'light-danger' ? 'danger'
              : (variant === 'light-warning' || type === 'task_escalation' || resolved.isInboundCall)
                ? 'warning'
                : 'primary',
          },
        }, {
          timeout: sticky ? false : 8000,
          closeOnClick: true,
          hideProgressBar: true,
          icon: false,
          id: toastId,
          onClick: () => {
            navigateFromFcmLink(host, link)
          },
        })
        return
      }

      // Fallback: OS notification while app is focused (no toast host).
      showOsNotification(title, body, { link }).catch(err => {
        console.warn('[fcm] foreground showOsNotification error:', err)
      })
    } catch (err) {
      console.warn('[fcm] foreground handler error:', err)
    }
  })
}
